import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutoScrollContainer,
  BotMessageContent,
  ChatFooterTemplate,
  CubeDotLoaderMini,
} from '@crisp/ai-agents-client/react/ui';
import '@crisp/ai-agents-client/react/ui/styles.css';
import {
  Button,
} from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSparkles,
  faSquare,
  faThumbsDown,
  faThumbsUp,
  faTurnDownLeft,
  faXmark,
  faRotate,
} from '@crisp/crisp-ui/icons/classic/regular';
import {
  AssistantApiError,
  streamAsk,
  tokenTextFromEvent,
  type AskCitation,
  type ConversationTurn,
} from '../../lib/assistantApi';
import {
  CITATION_HREF_PREFIX,
  linkifyCitationMarkers,
  toBotProjectedMessage,
} from '../../lib/assistantMessages';
import { submitAiFeedback } from '../../lib/api';
import { trackSearch } from '../../lib/analytics';
import { useCrispAuth } from '../../lib/auth0';
import { CitationCards, isNewTabClick } from './CitationCards';
import { DEFAULT_SITE, sitePathForSlug, type SiteConfig } from '../../lib/sites';
import { cn } from '../../lib/cn';
import styles from './AssistantPanel.module.css';

export interface AssistantPanelProps {
  isActive: boolean;
  resetKey: string | number | boolean;
  initialQuery?: string;
  seedQuery?: string;
  onSeedQueryConsumed?: () => void;
  pageSlug?: string;
  pageTitle?: string;
  site?: SiteConfig;
  onNavigate: (slug: string, anchor?: string) => void;
  onConversationChange?: (hasConversation: boolean) => void;
}

interface AssistantChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations: AskCitation[];
  queryId?: string;
  answerId?: string;
  feedback?: 'up' | 'down' | null;
  status: 'streaming' | 'complete' | 'error' | 'stopped';
  error?: string;
}

const HISTORY_LIMIT = 8;

function nextId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function toHistory(messages: AssistantChatMessage[]): ConversationTurn[] {
  return messages
    .filter((message) => {
      if (message.role === 'user') return message.content.trim().length > 0;
      return message.status === 'complete' && message.content.trim().length > 0;
    })
    .map((message) => ({ role: message.role, content: message.content }))
    .slice(-HISTORY_LIMIT);
}

function pageStarterPrompts(pageTitle?: string, siteName = 'Crisp'): string[] {
  if (pageTitle) {
    return [
      `Summarize ${pageTitle}`,
      `What are the key steps in ${pageTitle}?`,
      `What should I know before using ${pageTitle}?`,
    ];
  }
  return [
    `How do I get started with ${siteName}?`,
    `What can I do in ${siteName}?`,
    'Where can I find metric definitions?',
  ];
}

export const AssistantPanel: React.FC<AssistantPanelProps> = ({
  isActive,
  resetKey,
  initialQuery,
  seedQuery,
  onSeedQueryConsumed,
  pageSlug,
  pageTitle,
  site = DEFAULT_SITE,
  onNavigate,
  onConversationChange,
}) => {
  const { getAccessToken } = useCrispAuth();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const [messages, setMessages] = useState<AssistantChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const launchedInitialRef = useRef<string | null>(null);
  const consumedSeedRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    // A question sent right after this reset reads its history from the ref,
    // which would otherwise still hold the old conversation until the next render.
    messagesRef.current = [];
    setMessages([]);
    setDraft('');
    setIsStreaming(false);
    launchedInitialRef.current = null;
    consumedSeedRef.current = null;
  }, []);

  useEffect(() => {
    resetConversation();
  }, [resetKey, resetConversation]);

  const sendMessage = useCallback(
    async (rawQuery: string, historyOverride?: ConversationTurn[]) => {
      const query = rawQuery.trim();
      if (!query || abortRef.current) return;

      const controller = new AbortController();
      abortRef.current = controller;

      const history = historyOverride ?? toHistory(messagesRef.current);
      const userMessage: AssistantChatMessage = {
        id: nextId('user'),
        role: 'user',
        content: query,
        citations: [],
        status: 'complete',
      };
      const assistantId = nextId('assistant');
      const assistantMessage: AssistantChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        citations: [],
        status: 'streaming',
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setDraft('');
      setIsStreaming(true);
      trackSearch(query, 1, 'ai');

      const updateAssistant = (patch: Partial<AssistantChatMessage>) => {
        setMessages((prev) =>
          prev.map((message) => (message.id === assistantId ? { ...message, ...patch } : message))
        );
      };

      try {
        await streamAsk({
          query,
          site: site.id,
          conversationHistory: history,
          pageContext: pageSlug ? { slug: pageSlug, title: pageTitle } : null,
          getAccessToken: getAccessTokenRef.current,
          signal: controller.signal,
          onEvent: (event) => {
            if (event.type === 'token') {
              const token = tokenTextFromEvent(event);
              if (!token) return;
              setMessages((prev) =>
                prev.map((message) =>
                  message.id === assistantId
                    ? { ...message, content: message.content + token }
                    : message
                )
              );
            } else if (event.type === 'citations') {
              updateAssistant({
                citations: event.citations || [],
                queryId: event.query_id,
                answerId: event.answer_id,
              });
            } else if (event.type === 'done') {
              updateAssistant({
                status: 'complete',
                queryId: event.query_id,
                answerId: event.answer_id,
              });
            } else if (event.type === 'error') {
              updateAssistant({
                status: 'error',
                error: event.content || 'The assistant could not generate an answer.',
              });
            }
          },
        });

        setMessages((prev) =>
          prev.map((message) => {
            if (message.id !== assistantId) return message;
            if (message.status === 'streaming') {
              return { ...message, status: 'complete' };
            }
            return message;
          })
        );
      } catch (error) {
        const aborted =
          (error instanceof AssistantApiError && error.aborted) || controller.signal.aborted;
        setMessages((prev) =>
          prev.map((message) => {
            if (message.id !== assistantId) return message;
            if (aborted) {
              return {
                ...message,
                status: message.content.trim() ? 'stopped' : 'error',
                error: message.content.trim()
                  ? 'Generation stopped.'
                  : 'Generation stopped before an answer was produced.',
              };
            }
            const messageText =
              error instanceof AssistantApiError
                ? error.message
                : 'The assistant is unavailable. Please try again.';
            return {
              ...message,
              status: 'error',
              error: messageText,
            };
          })
        );
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        setIsStreaming(false);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    },
    [pageSlug, pageTitle, site.id]
  );

  useEffect(() => {
    if (!isActive) return;
    const pending = initialQuery?.trim();
    if (!pending) return;
    if (launchedInitialRef.current === pending) return;
    launchedInitialRef.current = pending;
    void sendMessage(pending);
  }, [isActive, initialQuery, sendMessage]);

  useEffect(() => {
    if (!isActive) return;
    const pending = seedQuery?.trim();
    if (!pending) return;
    if (consumedSeedRef.current === pending) return;
    consumedSeedRef.current = pending;
    onSeedQueryConsumed?.();
    void sendMessage(pending);
  }, [isActive, seedQuery, onSeedQueryConsumed, sendMessage]);

  useEffect(() => {
    if (isActive) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(timer);
    }
  }, [isActive, messages.length]);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const retryLast = useCallback(() => {
    const current = messagesRef.current;
    const lastUserIndex = [...current].map((m) => m.role).lastIndexOf('user');
    if (lastUserIndex < 0) return;
    const lastUser = current[lastUserIndex];
    const history = toHistory(current.slice(0, lastUserIndex));
    setMessages(current.slice(0, lastUserIndex));
    void sendMessage(lastUser.content, history);
  }, [sendMessage]);

  const handleFeedback = useCallback(async (message: AssistantChatMessage, vote: 'up' | 'down') => {
    setMessages((prev) =>
      prev.map((item) => (item.id === message.id ? { ...item, feedback: vote } : item))
    );
    try {
      let token: string | null = null;
      if (getAccessTokenRef.current) {
        try {
          token = await getAccessTokenRef.current();
        } catch {
          token = null;
        }
      }
      await submitAiFeedback(message.queryId || 'unknown', message.answerId || 'unknown', vote, token);
    } catch (err) {
      console.warn('Failed to submit AI feedback:', err);
    }
  }, []);

  const citationHref = useCallback(
    (slug: string, anchor?: string) => {
      const base = sitePathForSlug(site, slug);
      return anchor ? `${base}#${anchor}` : base;
    },
    [site]
  );

  // BotMessageContent renders its markdown through dangerouslySetInnerHTML, so
  // the marker links cannot carry React handlers; catch their clicks on the way
  // out instead. Keyboard activation bubbles a click too, so this covers both.
  const handleCitationMarkerClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, citations: AskCitation[]) => {
      const marker = (event.target as HTMLElement).closest?.(
        `a[href^="${CITATION_HREF_PREFIX}"]`
      );
      if (!marker) return;

      event.preventDefault();
      const id = Number(marker.getAttribute('href')?.slice(CITATION_HREF_PREFIX.length));
      const citation = citations.find((entry) => entry.id === id);
      if (!citation) return;
      if (isNewTabClick(event)) {
        window.open(citationHref(citation.slug, citation.anchor || undefined), '_blank', 'noopener');
      } else {
        onNavigate(citation.slug, citation.anchor || undefined);
      }
    },
    [onNavigate, citationHref]
  );

  // The renderer replaces the text of any non-absolute link with its href, so
  // the markers would read "#cite-3". It also rewrites its HTML while an answer
  // streams, which is why this watches the DOM instead of patching it once.
  const transcriptRef = useRef<HTMLDivElement>(null);
  const hasMessages = messages.length > 0;
  useEffect(() => {
    onConversationChange?.(hasMessages);
  }, [hasMessages, onConversationChange]);

  useEffect(() => {
    const transcript = transcriptRef.current;
    if (!transcript) return;
    const relabel = () => {
      transcript
        .querySelectorAll<HTMLAnchorElement>(`a[href^="${CITATION_HREF_PREFIX}"]`)
        .forEach((marker) => {
          const id = marker.getAttribute('href')?.slice(CITATION_HREF_PREFIX.length) ?? '';
          if (marker.textContent !== id) marker.textContent = id;
          if (marker.hasAttribute('target')) marker.removeAttribute('target');
        });
    };
    relabel();
    const observer = new MutationObserver(relabel);
    observer.observe(transcript, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [hasMessages]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isStreaming || !draft.trim()) return;
    void sendMessage(draft);
  };

  const starters = useMemo(
    () => pageStarterPrompts(pageTitle, site.name),
    [pageTitle, site.name]
  );
  const lastAssistant = [...messages].reverse().find((message) => message.role === 'assistant');
  const showRetry = Boolean(
    lastAssistant && (lastAssistant.status === 'error' || lastAssistant.status === 'stopped') && !isStreaming
  );

  const composer = (
    <ChatFooterTemplate>
      <form onSubmit={handleSubmit} className={styles.composer}>
        <FontAwesomeIcon icon={faSparkles} className={`icon-slot ${styles.sparkle}`} />
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={
            messages.length === 0
              ? `Ask anything about ${site.name}...`
              : 'Ask a follow-up question...'
          }
          aria-label={messages.length === 0 ? 'Ask AI' : 'Ask a follow-up question'}
          disabled={isStreaming}
          className={styles.composerInput}
        />
        {draft && !isStreaming && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => setDraft('')}
            aria-label="Clear question"
            iconPrimary={<FontAwesomeIcon icon={faXmark} />}
          />
        )}
        {isStreaming ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={stopStreaming}
            iconPrimary={<FontAwesomeIcon icon={faSquare} />}
          >
            Stop
          </Button>
        ) : (
          <Button
            type="submit"
            size="sm"
            disabled={!draft.trim()}
            iconPrimary={<FontAwesomeIcon icon={faTurnDownLeft} />}
          >
            {messages.length === 0 ? 'Ask' : 'Send'}
          </Button>
        )}
      </form>
    </ChatFooterTemplate>
  );

  return (
    <div className={cn('ask-ai-theme', styles.root)}>
      {messages.length === 0 ? (
        <>
          <div className={styles.empty}>
            <FontAwesomeIcon icon={faSparkles} className={`icon-slot ${styles.sparkle}`} />
            <p className={styles.emptyTitle}>Ask AI</p>
            <p className={styles.emptyBody}>
              Get answers with citations from {site.name} documentation
              {pageTitle ? ` while you read ${pageTitle}` : ''}.
            </p>
            <div className={styles.starters}>
              {starters.map((prompt) => (
                <Button
                  key={prompt}
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => void sendMessage(prompt)}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
          {composer}
        </>
      ) : (
        <div className={styles.thread}>
          <AutoScrollContainer className="flex-1 min-h-0" footer={composer}>
            <div ref={transcriptRef} className={styles.transcript} aria-live="polite">
              {messages.map((message) => {
                if (message.role === 'user') {
                  return (
                    <div key={message.id} className={styles.userRow}>
                      <div className={styles.userBubble}>{message.content}</div>
                    </div>
                  );
                }

                const projected = toBotProjectedMessage(
                  message.id,
                  linkifyCitationMarkers(
                    message.content,
                    message.citations.map((citation) => citation.id)
                  ),
                  message.status === 'streaming'
                );
                const busy = message.status === 'streaming';

                return (
                  <div
                    key={message.id}
                    aria-busy={busy}
                    role={message.status === 'error' ? 'alert' : 'status'}
                  >
                    {message.content ? (
                      <div
                        className={styles.botBubble}
                        onClick={(event) => handleCitationMarkerClick(event, message.citations)}
                      >
                        <BotMessageContent message={projected} />
                        {busy && (
                          <span className={styles.busy}>
                            <CubeDotLoaderMini size={14} />
                            <span>Generating answer…</span>
                          </span>
                        )}
                      </div>
                    ) : busy ? (
                      <div className={styles.busy}>
                        <CubeDotLoaderMini size={16} />
                        <span>Generating answer…</span>
                      </div>
                    ) : null}

                    {message.status === 'error' && (
                      <div className={styles.error}>
                        <span>
                          {message.error || 'The assistant is unavailable. Please try again.'}
                        </span>
                        {showRetry && message.id === lastAssistant?.id && (
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={retryLast}
                            iconPrimary={<FontAwesomeIcon icon={faRotate} />}
                          >
                            Retry
                          </Button>
                        )}
                      </div>
                    )}

                    {message.status === 'stopped' && message.error && (
                      <div className={styles.stopped}>
                        <span>{message.error}</span>
                        {showRetry && message.id === lastAssistant?.id && (
                          <Button
                            type="button"
                            variant="link"
                            size="xs"
                            onClick={retryLast}
                            iconPrimary={<FontAwesomeIcon icon={faRotate} />}
                          >
                            Retry
                          </Button>
                        )}
                      </div>
                    )}

                    <CitationCards
                      citations={message.citations}
                      hrefFor={citationHref}
                      onNavigate={onNavigate}
                    />

                    {(message.status === 'complete' || message.status === 'stopped') &&
                      message.content && (
                        <div className={styles.feedback}>
                          <span>Was this answer helpful?</span>
                          <div>
                            <Button
                              type="button"
                              variant={message.feedback === 'up' ? 'default' : 'ghost'}
                              size="icon-xs"
                              aria-label="Mark answer helpful"
                              onClick={() => void handleFeedback(message, 'up')}
                              iconPrimary={<FontAwesomeIcon icon={faThumbsUp} />}
                            />
                            <Button
                              type="button"
                              variant={message.feedback === 'down' ? 'secondary' : 'ghost'}
                              size="icon-xs"
                              aria-label="Mark answer not helpful"
                              onClick={() => void handleFeedback(message, 'down')}
                              iconPrimary={<FontAwesomeIcon icon={faThumbsDown} />}
                            />
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </AutoScrollContainer>
        </div>
      )}
    </div>
  );
};

export default AssistantPanel;
